import {defineStore} from 'pinia';
import {QueryItem} from '@/core/types';
import {SanitizeValue} from '@/core/utils';
import {SmartSearchConfig} from "@/main";

const RECENT_QUERY_SAVE_DELAY = 300;

export const useUserRecentQueriesStore = defineStore('userRecentQueries', {
  state: () => ({
    queries: [] as Array<QueryItem>,
    localStorageKey: 'SSv3_userRecentQueries',
    pendingSaveTimeout: null as ReturnType<typeof setTimeout> | null,
    lastAutoSavedQuery: null as string | null
  }),
  
  actions: {
    clearPendingSaveTimeout() {
      if (this.pendingSaveTimeout) {
        clearTimeout(this.pendingSaveTimeout);
        this.pendingSaveTimeout = null;
      }
    },
    
    addQuery(query: string, options: { source?: 'auto' | 'manual' } = {}) {
      const sanitizedQuery = SanitizeValue(query).trim();
      if (!sanitizedQuery.length) {
        this.clearPendingSaveTimeout();
        return;
      }
      
      const source = options?.source ?? 'auto';
      if (source === 'auto') {
        if (this.pendingSaveTimeout) {
          clearTimeout(this.pendingSaveTimeout);
        }
        this.pendingSaveTimeout = window.setTimeout(() => {
          this.pendingSaveTimeout = null;
          this.persistQuery(sanitizedQuery, source);
        }, RECENT_QUERY_SAVE_DELAY);
        return;
      }
      
      this.clearPendingSaveTimeout();
      this.persistQuery(sanitizedQuery, source);
    },
    
    persistQuery(sanitizedQuery: string, source: 'auto' | 'manual') {
      if (source === 'auto') {
        if (this.lastAutoSavedQuery && this.lastAutoSavedQuery !== sanitizedQuery) {
          this.queries = this.queries.filter(q => q.query !== this.lastAutoSavedQuery);
        }
        this.lastAutoSavedQuery = sanitizedQuery;
      } else if (source === 'manual' && this.lastAutoSavedQuery) {
        this.queries = this.queries.filter(q => q.query !== this.lastAutoSavedQuery);
        this.lastAutoSavedQuery = null;
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const recentQueriesCount = SmartSearchConfig.value.onfocus.recentQueries.count;
      const newQuery = {query: sanitizedQuery};
      this.queries = this.queries.filter(q => q.query !== sanitizedQuery);
      this.queries.unshift(newQuery);
      if (this.queries.length > recentQueriesCount) {
        this.queries.pop();
      }
      this.saveQueries();
    },
    
    removeQuery(index: number) {
      this.clearPendingSaveTimeout();
      setTimeout(()=>{
        const removed = this.queries.splice(index, 1);
        this.saveQueries();
        if (removed[0]?.query === this.lastAutoSavedQuery) {
          this.lastAutoSavedQuery = null;
        }
      },500)
      
    },
    
    clearQueries() {
      this.clearPendingSaveTimeout();
      this.queries = [];
      this.saveQueries();
      this.lastAutoSavedQuery = null;
    },
    
    saveQueries() {
      const uniqueQueries: QueryItem[] = [];
      const seenQueries = new Set<string>();

      this.queries.forEach(item => {
        const normalizedQuery = typeof item?.query === 'string' ? item.query.trim() : '';
        if (!normalizedQuery.length || seenQueries.has(normalizedQuery)) {
          return;
        }
        seenQueries.add(normalizedQuery);
        uniqueQueries.push({ query: normalizedQuery });
      });

      try {
        localStorage.setItem(this.localStorageKey, JSON.stringify(uniqueQueries));
      } catch (error) {
        console.warn('Failed to persist recent queries', error);
      }
    },
    
    getQueries() {
      const queriesFromStorage = localStorage.getItem(this.localStorageKey);
      if (!queriesFromStorage) {
        return;
      }

      try {
        const parsedQueries = JSON.parse(queriesFromStorage) as Array<QueryItem>;
        this.queries = parsedQueries
          .map(item => (typeof item?.query === 'string' ? { query: SanitizeValue(item.query) } : null))
          .filter((item): item is QueryItem => Boolean(item));
        this.lastAutoSavedQuery = null;
      } catch (error) {
        console.warn('Failed to read recent queries from storage', error);
        localStorage.removeItem(this.localStorageKey);
        this.queries = [];
        this.lastAutoSavedQuery = null;
      }
    },
    
  },
  
  getters: {
    hasRecentQueries: (state) => state.queries.length > 0,
    recentQueries: (state) => state.queries,
  },
});
